import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { getUserInfo, saveUserInfo } from "../utils/authStorage";
import { UserInfo } from "../types/auth";

interface UserContextValue {
  userInfo: UserInfo | null;
}

const UserContext = createContext<UserContextValue>({ userInfo: null });

function resolveInitialUserInfo(): UserInfo | null {
  const infoData = new URLSearchParams(window.location.search).get("info");
  if (infoData) {
    saveUserInfo(infoData);
  }
  return getUserInfo();
}

export function UserProvider({ children }: { children: ReactNode }) {
  // Consume the `?info` blob exactly ONCE on mount: persist + decode it so
  // permissions are available immediately, then hold userInfo in state. We do NOT
  // re-derive userInfo from the URL afterwards — otherwise stripping `info` from
  // the URL (below) would re-run resolution.
  const [userInfo] = useState<UserInfo | null>(resolveInitialUserInfo);

  // Strip the `info` blob from the URL after it's been consumed, so the payload
  // doesn't linger in the address bar, history, or shareable links. Uses the
  // native History API (NOT react-router navigation) so it only rewrites the
  // address bar without issuing a route change — otherwise it would race with and
  // clobber permission-based route redirects.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has("info")) return;
    params.delete("info");
    const query = params.toString();
    const newUrl = window.location.pathname + (query ? `?${query}` : "") + window.location.hash;
    window.history.replaceState(window.history.state, "", newUrl);
  }, []);

  return <UserContext.Provider value={{ userInfo }}>{children}</UserContext.Provider>;
}

export function useUserContext(): UserContextValue {
  return useContext(UserContext);
}
